import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, Tooltip } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { toast } from 'react-toastify';
import { fetchFaqs, addFaq, updateFaq, deleteFaq } from '../store/slices/faqsSlice';
import EnhancedTable from '../components/common/EnhancedTable';
import DeleteConfirmDialog from '../components/dialogs/DeleteConfirmDialog';
import FaqDialog from '../components/dialogs/FaqDialog';
import FaqViewModal from '../components/dialogs/FaqViewModal';

const Faqs: React.FC = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);
  const dispatch = useAppDispatch();
  const { items, loading, pagination } = useAppSelector(s => s.faqs);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState<any | null>(null);
  const [faqDialogOpen, setFaqDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [editingFaq, setEditingFaq] = useState<any | null>(null);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [faqViewModalOpen, setFaqViewModalOpen] = useState(false);
  const [faqToView, setFaqToView] = useState<any | null>(null);

  const openAdd = () => { setDialogMode('add'); setEditingFaq(null); setFaqDialogOpen(true); };
  const openEdit = (f: any) => { setDialogMode('edit'); setEditingFaq(f); setFaqDialogOpen(true); };
  const openView = (f: any) => { setFaqToView(f); setFaqViewModalOpen(true); };
  const closeView = () => { setFaqViewModalOpen(false); setFaqToView(null); };
  const handleDelete = (row: any) => {
    setFaqToDelete(row);
    setDeleteDialogOpen(true);
  };
  const handleConfirmDelete = async () => {
    if (faqToDelete) {
      setDeleteLoading(true);
      await dispatch(deleteFaq(faqToDelete.id));
      dispatch(fetchFaqs({ page: pagination.page, items_per_page: pagination.items_per_page }));
      toast.success('FAQ deleted successfully!');
      setDeleteLoading(false);
    }
    setDeleteDialogOpen(false);
    setFaqToDelete(null);
  };
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setFaqToDelete(null);
    setDeleteLoading(false);
  };
  const handleDialogSave = async (data: { section: string; question: string; answer: string }) => {
    if (dialogMode === 'edit' && editingFaq) {
      await dispatch(updateFaq({ id: editingFaq.id, ...data }));
      dispatch(fetchFaqs({ page: pagination.page, items_per_page: pagination.items_per_page }));
      toast.success('FAQ updated successfully!');
    } else {
      await dispatch(addFaq({ ...data, section: data.section }));
      dispatch(fetchFaqs({ page: pagination.page, items_per_page: pagination.items_per_page }));
      toast.success('FAQ added successfully!');
    }
    setFaqDialogOpen(false);
  };
  // Load FAQs with pagination, filter, sort
  const loadFaqs = useCallback(() => {
    const params: any = {
      page: pagination.page,
      items_per_page: pagination.items_per_page,
    };
    if (sortField) params.sort = `${sortField} ${sortDirection}`;
    dispatch(fetchFaqs(params));
  }, [dispatch, pagination.page, pagination.items_per_page, sortField, sortDirection]);

  useEffect(() => {
    loadFaqs();
  }, [loadFaqs]);

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    dispatch(fetchFaqs({ page: newPage, items_per_page: pagination.items_per_page }));
  };
  const handleItemsPerPageChange = (newPageSize: number) => {
    dispatch(fetchFaqs({ page: 1, items_per_page: newPageSize }));
  };
  // Sorting handler
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    // No local page state; fetchFaqs will handle pagination
  };

  const columns = [
    {
      id: 'section',
      label: 'Section',
      minWidth: 150,
      draggable: true,
      format: (value: string) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>{value ?? ''}</Typography>
      )
    },
    {
      id: 'question',
      label: 'Question',
      minWidth: 250,
      draggable: true,
      format: (value: string) => {
        if (!value) return '';
        const truncated = value.length > 60 ? value.slice(0, 60) + '…' : value;
        return (
          <Tooltip title={value} placement="top-start">
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', cursor: 'help' }}>{truncated}</Typography>
          </Tooltip>
        );
      }
    },
    {
      id: 'answer',
      label: 'Answer',
      minWidth: 300,
      draggable: true,
      format: (value: string) => {
        if (!value) return '';
        const truncated = value.length > 80 ? value.slice(0, 80) + '…' : value;
        return (
          <Tooltip title={value} placement="top-start">
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', cursor: 'help' }}>{truncated}</Typography>
          </Tooltip>
        );
      }
    }
  ];

  return (
    <Box sx={{ px: 3, py: 2.5, backgroundColor: 'background.default', minHeight: '100vh', position: 'relative' }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 3,
        p: 2,
        borderRadius: 3,
        background: '#fff',
        boxShadow: '0 4px 18px -4px rgba(0,0,0,0.12)',
        border: '1px solid',
        borderColor: 'grey.200',
      }}>
        <Box>
          <Typography variant="h5" fontWeight={800} letterSpacing={.5}>FAQs</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>
            Manage frequently asked questions for users.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
          <Button variant="contained" color="warning" startIcon={<AddIcon />} onClick={openAdd} sx={{ borderRadius: 2, fontWeight: 600, boxShadow: '0 4px 12px -2px rgba(0,0,0,0.18)', '&:hover': { boxShadow: '0 6px 16px -2px rgba(0,0,0,0.25)' } }}>Add FAQ</Button>
        </Box>
      </Box>

    <Box sx={{ mt: -1 }}>
      <EnhancedTable
        columns={columns as any}
        data={items}
        loading={loading}
        onEdit={(row: any) => openEdit(row)}
        onDelete={(row: any) => handleDelete(row)}
        onView={(row: any) => openView(row)}
        showStatusChangeCondition={() => false}
        showViewCondition={() => true}
        enableColumnReordering
        onColumnReorder={() => {}}
        showSelection={false}
        enableStickyFilters={false}
        useExternalPagination
        pagination={pagination}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        useExternalSorting
        onSort={handleSort}
        currentSortField={sortField}
        currentSortDirection={sortDirection}
      />
    </Box>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
        title="Delete FAQ"
        message="Are you sure you want to delete this FAQ?"
        itemName={faqToDelete?.question || ''}
        itemType="FAQ"
      />
      <FaqDialog
        open={faqDialogOpen}
        onClose={() => setFaqDialogOpen(false)}
        mode={dialogMode}
        faq={editingFaq}
        sections={Array.from(new Set(items.map(i => i.section)))}
        onSave={handleDialogSave}
      />
      {/* FAQ View Modal */}
      <FaqViewModal
        open={faqViewModalOpen}
        onClose={closeView}
        faq={faqToView}
      />
    </Box>
  );
};

export default Faqs;
